import React, { useCallback, useEffect, useState } from 'react';

import { useFormik } from 'formik';
import _ from 'lodash';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { reportDetail, reportExport } from 'api/endpoints/report';
import Breadcrumb from 'components/Breadcrumb';
import Button from 'components/Button';
import LayoutReview from 'components/layouts/LayoutReview';
import { ENUM_SURVEY_STATUS, ENUM_SURVEY_TYPE, ENUM_TYPE_ACTION, ENUM_TYPE_DOWNLOAD } from 'constants/enum';
import { LIST_TYPE_SURVEY } from 'constants/index';
import { TEXT_COMMON, TEXT_CREATE_SURVEY, TEXT_DOWNLOAD, TEXT_HEADER, TEXT_REPORT_LIST, TEXT_RESULT_REPORT } from 'constants/string/texts';
import { toastError } from 'lib/toast';
import ResultReport from 'pages/Report/ViewExam/ResultReport/ResultReport';
import Form from 'pages/Survey/Form/Form';
import AppRoutes from 'routes/routes';
import { setLoading } from 'store/actions/common';
import { calculatorHeightContain, handleError, handleImageSurvey } from 'utils/helper';

const DownloadFile = require('assets/resources/DownloadFile.svg').default;
const MoreVert = require('assets/resources/MoreVert.svg').default;

const ViewExample = () => {
  const { id } = useParams();
  const navigator = useNavigate();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { me } = useSelector((state) => state.auth);
  const [breadcrumbs, setBreadcrumbs] = useState([{ title: TEXT_HEADER.ANSWER_LIST, event: () => navigator(AppRoutes.report) }]);
  const [dataReport, setDataReport] = useState({});
  const [analysisContent, setAnalysisContent] = useState({});
  const [heightReportDetail, setHeightReportDetail] = useState(0);

  const initialValues = (() => {
    const initData = {
      title: '',
      description: '',
      status: ENUM_SURVEY_STATUS.DRAFT,
      survey_type: ENUM_SURVEY_TYPE.EXAM,
      sections: [
        {
          title: '',
          order: 0,
          questions: [],
        },
      ],
    };

    return initData;
  })();

  const form = useFormik({ initialValues: initialValues });

  const handleImage = async (images) => {
    return await handleImageSurvey(images);
  };

  const handleChildQuestion = async (subQuestion, survey_response) => {
    const childQuestions = [];
    for (const childQuestion of subQuestion) {
      const childQuestionHandled = await handleQuestion(childQuestion, survey_response);
      childQuestions.push(childQuestionHandled);
    }

    return childQuestions;
  };

  const handleSubQuestion = async (subQuestions, survey_response) => {
    const subQuestionsHandled = [];
    for (const subQuestion of subQuestions) {
      const subQuestionHandled = await handleQuestion(subQuestion, survey_response);

      if (subQuestion.child_questions && subQuestion.child_questions.length > 0) {
        const childQuestions = await handleChildQuestion(subQuestion.child_questions, survey_response);
        subQuestionHandled.child_questions = childQuestions;
      }
      subQuestionsHandled.push(subQuestionHandled);
    }
    return subQuestionsHandled;
  };

  const handleQuestion = async (question, survey_response) => {
    const processedImages = await handleImage(question.images);
    if (question.sub_questions && question.sub_questions.length > 0) {
      const subQuestions = await handleSubQuestion(question.sub_questions, survey_response);
      question.sub_questions = subQuestions;
    }

    return {
      ...question,
      action: ENUM_TYPE_ACTION.UPDATE,
      images: processedImages,
      answer: _.find(survey_response, { question_id: question.id })?.answer || null,
      media_files: _.find(survey_response, { question_id: question.id })?.media_files || null,
      video_category: _.find(survey_response, { question_id: question.id })?.video_category || null,
    };
  };

  const handleSection = async (section, survey_response) => {
    const processedQuestions = [];
    for (const question of section.questions) {
      const questionHandled = await handleQuestion(question, survey_response);
      processedQuestions.push(questionHandled);
    }
    return processedQuestions;
  };

  const handleSurvey = async (survey_content, survey_response) => {
    const dataSection = [];
    const sortedSections = _.sortBy(survey_content.sections, 'order');

    for (const section of sortedSections) {
      const sectionHandled = await handleSection(section, survey_response);
      dataSection.push({
        ...section,
        action: ENUM_TYPE_ACTION.UPDATE,
        questions: sectionHandled,
      });
    }

    for (const section of dataSection) {
      let questionCount = 0;
      for (const question of section.questions) {
        question.question_number = questionCount + 1;
        questionCount++;
      }
    }

    if (breadcrumbs.length === 1) {
      let title = _.find(LIST_TYPE_SURVEY, { value: survey_content.period_type })?.text;
      if (survey_content.is_template || survey_content.meta_data?.from_template) {
        title = `${TEXT_COMMON.FV_TEMPLATE}${survey_content.title}・${TEXT_REPORT_LIST.REPORT}`;
      }
      setBreadcrumbs([...breadcrumbs, { title: title }]);
    }

    form.setValues({ ...survey_content, sections: dataSection });
  };

  const handleSubAnswer = async (subAnswers) => {
    const subs = [];
    for (const sub of subAnswers) {
      subs.push({
        ...sub,
        question_id: sub.sub_question_id,
        sub_questions: sub.child_questions || [],
      });
    }
    const _answer = await handleAnswer(subs);

    return _answer;
  };

  const handleAnswer = async (answers) => {
    const processedAnswers = [];
    for (const answer of answers) {
      if (answer.sub_questions && answer.sub_questions.length > 0) {
        const subAnswers = await handleSubAnswer(answer.sub_questions);
        processedAnswers.push(...subAnswers);
      }
      const _answer = _.omit(answer, 'sub_questions');
      processedAnswers.push(_answer);
    }

    return processedAnswers;
  };

  const getDetail = async () => {
    dispatch(setLoading({ isOpen: true }));
    try {
      const result = await reportDetail(id);
      if (result.data) {
        setDataReport(result.data);
        setAnalysisContent(result.data.analysis_content);
        const survey_content = result.data.survey_content[0];
        const survey_response = await handleAnswer(result.data.report_content[0].responses);
        await handleSurvey(survey_content, survey_response);
      } else {
        navigate(AppRoutes.report);
      }
    } catch (err) {
      handleError(err);
    }
    dispatch(setLoading({ isOpen: false }));
  };

  const getReportDetail = useCallback(async () => {
    if (me) {
      await getDetail();
    }
  }, [me, id]);

  const handleDownload = async (type) => {
    dispatch(setLoading({ isOpen: true }));
    await reportExport({ report_ids: [id] }, type, me.lang)
      .then((res) => {
        dispatch(setLoading({ isOpen: false }));
      })
      .catch((error) => {
        toastError(error.message);
        dispatch(setLoading({ isOpen: false }));
      });
  };

  useEffect(() => {
    getReportDetail();
  }, [getReportDetail]);

  useEffect(() => {
    const height = calculatorHeightContain(
      [{ id: 'report-detail-title' }, { id: 'breadcrumb-download' }],
      [{ id: 'main' }, { id: 'layout-report-detail' }, { id: 'report-detail-container' }]
    );
    setHeightReportDetail(height);
  }, []);

  useEffect(() => {
    document.body.classList.add('overflow-hidden-custom');
    document.body.classList.add('h-screen-custom');

    return () => {
      document.body.classList.remove('overflow-hidden-custom');
      document.body.classList.remove('h-screen-custom');
    };
  }, []);

  return (
    <>
      <section id="create-survey" className="flex-middle-start flex-wrap relative">
        <div className="flex justify-center items-start space-x-6" id="breadcrumb-download">
          <div className="w-report-detail">
            <div className="flex items-center gap-2">
              <div className="max-w-96 flex items-center gap-2">
                <Breadcrumb breadcrumbs={breadcrumbs} />
              </div>
            </div>
          </div>
          <div className="w-160">
            <div className="flex items-center justify-end gap-8 relative z-2">
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => handleDownload(ENUM_TYPE_DOWNLOAD.CSV)}
                  type="button"
                  color="white"
                  className="text-black/85 text-sm w-55.5 h-8"
                  text={TEXT_DOWNLOAD.CSV_DOWNLOAD}
                  icon={<img className="mr-2.5" src={DownloadFile} alt="download file icon" width={14} height={14} />}
                />
                <Button
                  onClick={() => handleDownload(ENUM_TYPE_DOWNLOAD.EXCEL)}
                  type="button"
                  color="white"
                  className="text-black/85 text-sm w-55.75 h-8"
                  text={TEXT_DOWNLOAD.EXCEL_DOWNLOAD}
                  icon={<img className="mr-2.5" src={DownloadFile} alt="download file icon" width={14} height={14} />}
                />

                <div className="relative flex items-center">
                  <button type="button" className="w-6 h-8">
                    <img src={MoreVert} alt="more vert icon" width={24} height={24} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="report-detail-container" className="flex justify-center items-start space-x-6 pt-6.5">
          <div className="w-report-detail">
            <div className="pb-4" id="report-detail-title">
              <h1 className="text-2xl font-bold text-black-102 text-center">{TEXT_RESULT_REPORT.ANSWER_RESULT}</h1>
            </div>
            <LayoutReview className="max-w-full !px-0" id="layout-report-detail">
              <form id="report-detail-form" className="create-survey overflow-y-auto px-8" style={{ height: heightReportDetail }}>
                <div className="flex items-center space-x-6 text-black/85 text-sm mb-8">
                  {form.values.title && (
                    <span>
                      {TEXT_CREATE_SURVEY.SURVEY_TITLE}
                      <Link to={AppRoutes.surveyViewFn(form.values.id)} className="text-primary-346 underline underline-offset-3">
                        {`${form.values.is_template || form.values.meta_data?.from_template ? TEXT_COMMON.FV_TEMPLATE : ''}${form.values.title}`}
                      </Link>
                    </span>
                  )}
                  {dataReport.child_id && (
                    <span>
                      {TEXT_REPORT_LIST.USER_ID}：{dataReport.child_id}
                    </span>
                  )}
                  {dataReport.created_at && (
                    <span>
                      {TEXT_REPORT_LIST.ANSWER_DAY}：{moment(dataReport.created_at).format('YYYY/MM/DD')}
                    </span>
                  )}
                </div>
                <Form form={form} viewOnly={true} showControlMenu={false} showTitle={false} />
              </form>
            </LayoutReview>
          </div>

          <div className="w-160">
            <div className="pb-4">
              <h1 className="text-2xl font-bold text-black-102 text-center">{TEXT_RESULT_REPORT.ANALYSIS_REPORT}</h1>
            </div>
            <LayoutReview className="max-w-full !px-0">
              <div id="report-detail-analysis" className="create-survey overflow-y-auto px-8" style={{ maxHeight: heightReportDetail }}>
                <ResultReport dataReport={dataReport.child_id} analysisContent={analysisContent} />
              </div>
            </LayoutReview>
          </div>
        </div>
      </section>
    </>
  );
};

export default React.memo(ViewExample);
